import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getAuth } from 'firebase/auth';
import {
  ShieldExclamationIcon,
  ClockIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  UserIcon,
  BanknotesIcon,
  EyeIcon,
  XMarkIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { FraudPreventionService, CLEARANCE_STATUS } from '../../services/FraudPreventionService';
import { PayoutService } from '../../services/PayoutService';
import RiskBadge from '../../components/fraud/RiskBadge';
import TrustTierBadge from '../../components/fraud/TrustTierBadge';

const fraudService = new FraudPreventionService();
const payoutService = new PayoutService();

/* ─────────── Stat Card ─────────── */
function StatCard({ icon: Icon, iconColor, bgColor, label, value, sub, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-white dark:bg-[#111111] rounded-xl border border-gray-100 dark:border-[#1A1A1A] p-5 hover:border-brand/30 transition-colors cursor-default"
    >
      <div className="flex items-start gap-3.5">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${bgColor}`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{value}</p>
          <p className="text-xs text-gray-500 dark:text-[#999999] mt-0.5">{label}</p>
          {sub && (
            <p className="text-[10px] text-gray-400 dark:text-[#666666] mt-0.5">{sub}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ─────────── Empty State ─────────── */
function EmptyState({ icon: Icon, title, description }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6">
      <div className="w-14 h-14 rounded-2xl bg-brand/5 dark:bg-brand/10 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-brand/60" />
      </div>
      <p className="text-base font-medium text-gray-700 dark:text-gray-200">{title}</p>
      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">{description}</p>
    </div>
  );
}

/* ─────────── Clearance Row ─────────── */
function ClearanceRow({ clearance, onRelease, onFreeze, index }) {
  const eligibleDate = clearance.eligibleAt?.toDate
    ? clearance.eligibleAt.toDate()
    : clearance.eligibleAt ? new Date(clearance.eligibleAt) : null;

  const daysRemaining = eligibleDate
    ? Math.max(0, Math.ceil((eligibleDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  const progress = daysRemaining !== null
    ? Math.max(5, 100 - (daysRemaining / 7) * 100)
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.03 }}
      className="flex items-center gap-4 px-5 py-3.5 hover:bg-white/60 dark:hover:bg-white/5 transition-colors group"
    >
      {/* Avatar */}
      <div className="w-8 h-8 rounded-full bg-gray-100/80 dark:bg-[#2A2A2A] flex items-center justify-center text-xs font-medium text-gray-500 dark:text-gray-400 flex-shrink-0">
        {(clearance.userId || '?')[0]?.toUpperCase()}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-gray-900 dark:text-white truncate">
          {clearance.userId?.slice(0, 16)}...
        </p>
        <p className="text-[10px] text-gray-400 dark:text-[#666666] font-mono truncate">
          {clearance.invoiceId?.slice(0, 20) || '—'}
        </p>
      </div>

      {/* Amount */}
      <div className="text-right flex-shrink-0 w-20">
        <p className="text-[13px] font-semibold text-gray-900 dark:text-white tabular-nums">
          ${(clearance.paymentAmount || 0).toFixed(2)}
        </p>
      </div>

      {/* Risk */}
      <div className="flex-shrink-0 w-16">
        <RiskBadge level={clearance.riskLevel || 'LOW'} score={clearance.riskScore} showScore />
      </div>

      {/* Progress / Days */}
      <div className="flex-shrink-0 w-20">
        {daysRemaining !== null ? (
          <div className="space-y-1">
            <div className="w-full h-1 bg-gray-100 dark:bg-[#1A1A1A] rounded-full overflow-hidden">
              <div
                className="h-full bg-brand rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-[10px] text-gray-400 dark:text-[#666666] text-center tabular-nums">
              {daysRemaining}d remaining
            </p>
          </div>
        ) : (
          <p className="text-[10px] text-gray-400 dark:text-[#666666] text-center">—</p>
        )}
      </div>

      {/* Status pill */}
      <div className="flex-shrink-0 w-24">
        {clearance.status === CLEARANCE_STATUS.UNDER_REVIEW ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full font-medium bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400">
            <EyeIcon className="w-3 h-3" /> Review
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full font-medium bg-amber-50 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400">
            <ClockIcon className="w-3 h-3" /> Pending
          </span>
        )}
      </div>

      {/* Actions — visible on group hover */}
      <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <motion.button
          onClick={() => onRelease(clearance.id)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="p-1.5 rounded-lg text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 transition-colors"
          title="Release payout"
        >
          <CheckCircleIcon className="w-4 h-4" />
        </motion.button>
        <motion.button
          onClick={() => onFreeze(clearance.id)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
          title="Freeze payout"
        >
          <LockClosedIcon className="w-4 h-4" />
        </motion.button>
      </div>
    </motion.div>
  );
}

/* ─────────── Fraud Flag Row ─────────── */
function FlagRow({ flag, onResolve, index }) {
  const severityStyles = {
    high: 'bg-red-50 dark:bg-red-500/20 text-red-600 dark:text-red-400',
    medium: 'bg-amber-50 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400',
    low: 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400',
  };

  const createdAt = flag.createdAt?.toDate
    ? flag.createdAt.toDate()
    : flag.createdAt ? new Date(flag.createdAt) : null;

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className="flex items-center gap-4 px-5 py-3.5 hover:bg-white/60 dark:hover:bg-white/5 transition-colors group"
    >
      <div className="w-8 h-8 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center flex-shrink-0">
        <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-gray-900 dark:text-white truncate">
          {flag.userId?.slice(0, 16)}...
        </p>
        <p className="text-xs text-gray-500 dark:text-[#999999] mt-0.5 line-clamp-1">
          {flag.description}
        </p>
      </div>

      <span className={`inline-flex px-2 py-0.5 text-[11px] rounded-full font-medium flex-shrink-0 ${severityStyles[flag.severity] || severityStyles.medium}`}>
        {flag.severity}
      </span>

      <span className="text-[10px] text-gray-400 dark:text-[#666666] flex-shrink-0 w-20 text-right tabular-nums">
        {createdAt ? createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
      </span>

      <motion.button
        onClick={() => onResolve(flag.id)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="p-1.5 rounded-lg text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"
        title="Resolve"
      >
        <CheckCircleIcon className="w-4 h-4" />
      </motion.button>
    </motion.div>
  );
}

/* ─────────── Account Row ─────────── */
function AccountRow({ account, index, action }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.03 }}
      className="flex items-center gap-4 px-5 py-3.5 hover:bg-white/60 dark:hover:bg-white/5 transition-colors cursor-pointer group"
    >
      <div className="w-8 h-8 rounded-full bg-gray-100/80 dark:bg-[#2A2A2A] flex items-center justify-center text-xs font-medium text-gray-500 dark:text-gray-400 flex-shrink-0">
        {(account.userId || '?')[0]?.toUpperCase()}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-gray-900 dark:text-white truncate">
          {account.userId}
        </p>
        <p className="text-[10px] text-gray-400 dark:text-[#666666]">
          {account.totalPayments || 0} payments
          {account.frozenReason && (
            <span className="text-red-500 dark:text-red-400 ml-2">{account.frozenReason}</span>
          )}
        </p>
      </div>

      <RiskBadge level={account.riskLevel} score={account.riskScore} showScore />
      <TrustTierBadge tier={account.trustTier} />

      {account.accountFrozen && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full font-medium bg-red-50 dark:bg-red-500/20 text-red-600 dark:text-red-400">
          <LockClosedIcon className="w-3 h-3" /> Frozen
        </span>
      )}

      {action}

      <ChevronRightIcon className="w-4 h-4 text-gray-300 dark:text-[#333] group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors flex-shrink-0" />
    </motion.div>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
   Risk Dashboard — Main
   ═════════════════════════════════════════════════════════════════════════════ */
export default function RiskDashboard() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('clearances');
  const [pendingClearances, setPendingClearances] = useState([]);
  const [fraudFlags, setFraudFlags] = useState([]);
  const [highRiskAccounts, setHighRiskAccounts] = useState([]);
  const [frozenAccounts, setFrozenAccounts] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const auth = getAuth();

  const loadData = useCallback(async () => {
    try {
      const [clearances, flags, highRisk, frozen] = await Promise.all([
        fraudService.getPendingClearances(),
        fraudService.getAllActiveFraudFlags(),
        fraudService.getHighRiskAccounts(),
        fraudService.getFrozenAccounts(),
      ]);
      setPendingClearances(clearances);
      setFraudFlags(flags);
      setHighRiskAccounts(highRisk);
      setFrozenAccounts(frozen);
    } catch (error) {
      console.error('Error loading risk dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleRelease = async (id) => {
    await payoutService.adminReleasePayout(id, auth.currentUser?.uid);
    await loadData();
  };

  const handleFreeze = async (id) => {
    await fraudService.freezeClearance(id, 'Admin freeze');
    await loadData();
  };

  const handleResolveFlag = async (id) => {
    await fraudService.resolveFraudFlag(id, auth.currentUser?.uid, 'Reviewed and resolved');
    await loadData();
  };

  const handleProcessEligible = async () => {
    const released = await payoutService.processEligiblePayouts();
    if (released.length > 0) await loadData();
  };

  const handleUnfreeze = async (userId) => {
    await fraudService.unfreezeAccount(userId);
    await loadData();
  };

  const tabs = [
    { id: 'clearances', label: 'Clearances', count: pendingClearances.length },
    { id: 'flags', label: 'Fraud Flags', count: fraudFlags.length },
    { id: 'accounts', label: 'High Risk', count: highRiskAccounts.length },
    { id: 'frozen', label: 'Frozen', count: frozenAccounts.length },
  ];

  /* ─── Loading skeleton ─── */
  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-6 animate-pulse">
        <div className="h-7 w-40 bg-gray-200/60 dark:bg-[#1A1A1A] rounded-lg" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-24 bg-gray-200/60 dark:bg-[#1A1A1A] rounded-xl" />
          ))}
        </div>
        <div className="h-64 bg-gray-200/60 dark:bg-[#1A1A1A] rounded-2xl" />
      </div>
    );
  }

  const totalHeld = pendingClearances.reduce((s, c) => s + (c.payoutAmount || 0), 0);
  const totalReserve = pendingClearances.reduce((s, c) => s + (c.reserveAmount || 0), 0);
  const highSeverityCount = fraudFlags.filter(f => f.severity === 'high').length;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* ─── Header ─── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">
            Risk Dashboard
          </h1>
          <p className="text-xs text-gray-400 dark:text-[#666666] mt-0.5">
            Payout risk, fraud flags, and account trust levels
          </p>
        </div>
        <div className="flex items-center gap-2">
          <motion.button
            onClick={handleProcessEligible}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-brand hover:bg-brand-hover rounded-full transition-all"
            style={{ boxShadow: '0 2px 8px rgba(249, 115, 22, 0.2)' }}
          >
            <CheckCircleIcon className="w-3.5 h-3.5" />
            Process Eligible
          </motion.button>
          <motion.button
            onClick={handleRefresh}
            disabled={refreshing}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-2 rounded-xl text-gray-400 dark:text-[#666666] hover:text-gray-600 dark:hover:text-white hover:bg-white dark:hover:bg-white/5 transition-all"
          >
            <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </motion.button>
        </div>
      </div>

      {/* ─── Stats ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={ClockIcon}
          iconColor="text-amber-500"
          bgColor="bg-amber-50 dark:bg-amber-500/10"
          label="Pending Clearances"
          value={pendingClearances.length}
          sub={`$${totalHeld.toFixed(2)} held`}
          delay={0}
        />
        <StatCard
          icon={ExclamationTriangleIcon}
          iconColor="text-red-500"
          bgColor="bg-red-50 dark:bg-red-500/10"
          label="Active Fraud Flags"
          value={fraudFlags.length}
          sub={highSeverityCount > 0 ? `${highSeverityCount} high severity` : null}
          delay={0.03}
        />
        <StatCard
          icon={ShieldExclamationIcon}
          iconColor="text-brand"
          bgColor="bg-brand/5 dark:bg-brand/10"
          label="High-Risk Accounts"
          value={highRiskAccounts.length}
          delay={0.06}
        />
        <StatCard
          icon={LockClosedIcon}
          iconColor="text-red-500"
          bgColor="bg-red-50 dark:bg-red-500/10"
          label="Frozen Accounts"
          value={frozenAccounts.length}
          sub={totalReserve > 0 ? `$${totalReserve.toFixed(2)} reserves` : null}
          delay={0.09}
        />
      </div>

      {/* ─── Tab Navigation (spring-animated indicator) ─── */}
      <div className="relative flex items-center gap-0.5 p-1 bg-gray-100/50 dark:bg-white/5 rounded-lg w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`relative px-3 py-1.5 text-xs transition-all rounded-md ${
              activeTab === tab.id
                ? 'text-gray-900 dark:text-white'
                : 'text-gray-400 dark:text-[#666666] hover:text-gray-600 dark:hover:text-gray-400'
            }`}
          >
            {activeTab === tab.id && (
              <motion.div
                layoutId="risk-tab-indicator"
                className="absolute inset-0 bg-white dark:bg-[#1A1A1A] rounded-md shadow-sm"
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            )}
            <span className="relative z-10 flex items-center gap-1.5">
              {tab.label}
              {tab.count > 0 && (
                <span className="text-[9px] text-gray-300/70 dark:text-gray-600">{tab.count}</span>
              )}
            </span>
          </button>
        ))}
      </div>

      {/* ─── Tab Content ─── */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="bg-white dark:bg-[#111111] rounded-2xl border border-gray-100 dark:border-[#1A1A1A] overflow-hidden"
        >
          {/* ── Clearances ── */}
          {activeTab === 'clearances' && (
            pendingClearances.length === 0 ? (
              <EmptyState
                icon={ClockIcon}
                title="No pending clearances"
                description="All payouts have been processed"
              />
            ) : (
              <div className="divide-y divide-gray-50 dark:divide-[#1A1A1A]">
                {pendingClearances.map((c, i) => (
                  <ClearanceRow
                    key={c.id}
                    clearance={c}
                    onRelease={handleRelease}
                    onFreeze={handleFreeze}
                    index={i}
                  />
                ))}
              </div>
            )
          )}

          {/* ── Fraud Flags ── */}
          {activeTab === 'flags' && (
            fraudFlags.length === 0 ? (
              <EmptyState
                icon={ShieldExclamationIcon}
                title="No active fraud flags"
                description="No suspicious activity detected"
              />
            ) : (
              <div className="divide-y divide-gray-50 dark:divide-[#1A1A1A]">
                {fraudFlags.map((f, i) => (
                  <FlagRow
                    key={f.id}
                    flag={f}
                    onResolve={handleResolveFlag}
                    index={i}
                  />
                ))}
              </div>
            )
          )}

          {/* ── High-Risk Accounts ── */}
          {activeTab === 'accounts' && (
            highRiskAccounts.length === 0 ? (
              <EmptyState
                icon={ShieldExclamationIcon}
                title="No high-risk accounts"
                description="All accounts are within acceptable risk levels"
              />
            ) : (
              <div className="divide-y divide-gray-50 dark:divide-[#1A1A1A]">
                {highRiskAccounts.map((a, i) => (
                  <AccountRow key={a.id} account={a} index={i} />
                ))}
              </div>
            )
          )}

          {/* ── Frozen Accounts ── */}
          {activeTab === 'frozen' && (
            frozenAccounts.length === 0 ? (
              <EmptyState
                icon={LockClosedIcon}
                title="No frozen accounts"
                description="No accounts are currently frozen"
              />
            ) : (
              <div className="divide-y divide-gray-50 dark:divide-[#1A1A1A]">
                {frozenAccounts.map((a, i) => (
                  <AccountRow
                    key={a.id}
                    account={a}
                    index={i}
                    action={
                      <motion.button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnfreeze(a.id);
                        }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="px-2.5 py-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors flex-shrink-0"
                      >
                        Unfreeze
                      </motion.button>
                    }
                  />
                ))}
              </div>
            )
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
